import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { apiClient, ACCESS_LEVELS } from '@rti/shared';
import type { AdminMenuItemPref } from '@rti/shared';
import { useAuth } from '@/contexts/AuthContext';
import {
  ADMIN_MENU_CATALOG,
  ADMIN_MENU_CATALOG_BY_KEY,
  ADMIN_MENU_DEFAULT_STARRED_ORDER,
  AdminMenuCatalogItem,
  canAccessAdminMenuItem,
} from '@/config/adminMenuCatalog';

export interface AdminMenuOrderedItem extends AdminMenuCatalogItem {
  starred: boolean;
}

interface AdminMenuContextValue {
  isLoading: boolean;
  orderedItems: AdminMenuOrderedItem[];
  starredItems: AdminMenuOrderedItem[];
  toggleStar: (key: string) => void;
  reorderStarred: (orderedKeys: string[]) => void;
  isStarred: (key: string) => boolean;
}

const AdminMenuContext = createContext<AdminMenuContextValue>({
  isLoading: true,
  orderedItems: [],
  starredItems: [],
  toggleStar: () => {},
  reorderStarred: () => {},
  isStarred: () => false,
});

const buildDefaultPrefs = (): AdminMenuItemPref[] =>
  ADMIN_MENU_DEFAULT_STARRED_ORDER
    .filter((key) => ADMIN_MENU_CATALOG_BY_KEY[key])
    .map((key) => ({ key, starred: true }));

const mergeWithCatalog = (prefs: AdminMenuItemPref[]): AdminMenuOrderedItem[] => {
  const seen = new Set<string>();
  const merged: AdminMenuOrderedItem[] = [];
  for (const pref of prefs) {
    const cat = ADMIN_MENU_CATALOG_BY_KEY[pref.key];
    if (!cat || seen.has(pref.key)) continue;
    seen.add(pref.key);
    merged.push({ ...cat, starred: !!pref.starred });
  }
  for (const cat of ADMIN_MENU_CATALOG) {
    if (seen.has(cat.key)) continue;
    merged.push({ ...cat, starred: cat.defaultStarred });
  }
  return merged;
};

const prefsFromOrderedItems = (items: AdminMenuOrderedItem[]): AdminMenuItemPref[] =>
  items.map((it) => ({ key: it.key, starred: it.starred }));

export const AdminMenuProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const isEmployee = user?.effective_membership_id === ACCESS_LEVELS.EMPLOYEE;
  const [orderedItems, setOrderedItems] = useState<AdminMenuOrderedItem[]>(() =>
    mergeWithCatalog(buildDefaultPrefs()),
  );
  const [isLoading, setIsLoading] = useState(true);
  const etagRef = useRef<string | null>(null);
  const saveTimer = useRef<number | null>(null);

  useEffect(() => {
    if (!isAuthenticated || (!isAdmin && !isEmployee)) {
      setOrderedItems(mergeWithCatalog(buildDefaultPrefs()));
      etagRef.current = null;
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    apiClient
      .getAdminMenuPreferences()
      .then((prefs) => {
        if (cancelled) return;
        etagRef.current = prefs.etag ?? null;
        setOrderedItems(mergeWithCatalog(prefs.items.length ? prefs.items : buildDefaultPrefs()));
      })
      .catch(() => {
        if (cancelled) return;
        setOrderedItems(mergeWithCatalog(buildDefaultPrefs()));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isAdmin, isEmployee]);

  const persist = useCallback((next: AdminMenuOrderedItem[]) => {
    if (saveTimer.current) {
      window.clearTimeout(saveTimer.current);
    }
    const payload = prefsFromOrderedItems(next);
    saveTimer.current = window.setTimeout(() => {
      apiClient
        .updateAdminMenuPreferences(payload, etagRef.current)
        .then((res) => {
          etagRef.current = res.etag ?? null;
        })
        .catch(() => {});
    }, 250);
  }, []);

  const toggleStar = useCallback(
    (key: string) => {
      setOrderedItems((prev) => {
        const next = prev.map((it) => (it.key === key ? { ...it, starred: !it.starred } : it));
        persist(next);
        return next;
      });
    },
    [persist],
  );

  const reorderStarred = useCallback(
    (orderedKeys: string[]) => {
      setOrderedItems((prev) => {
        const byKey = new Map(prev.map((it) => [it.key, it]));
        const reordered: AdminMenuOrderedItem[] = [];
        const used = new Set<string>();
        for (const key of orderedKeys) {
          const it = byKey.get(key);
          if (it && !used.has(key)) {
            reordered.push(it);
            used.add(key);
          }
        }
        for (const it of prev) {
          if (!used.has(it.key)) reordered.push(it);
        }
        persist(reordered);
        return reordered;
      });
    },
    [persist],
  );

  const value = useMemo<AdminMenuContextValue>(() => {
    const visibleItems = orderedItems.filter((it) => canAccessAdminMenuItem(it, { isAdmin, isEmployee }));
    const starredItems = visibleItems.filter((it) => it.starred);
    return {
      isLoading,
      orderedItems: visibleItems,
      starredItems,
      toggleStar,
      reorderStarred,
      isStarred: (key: string) => visibleItems.find((it) => it.key === key)?.starred ?? false,
    };
  }, [isLoading, orderedItems, toggleStar, reorderStarred, isAdmin, isEmployee]);

  return <AdminMenuContext.Provider value={value}>{children}</AdminMenuContext.Provider>;
};

export const useAdminMenu = () => useContext(AdminMenuContext);
